import { useMemo } from "react";
import { useRouter } from "next/router";

import { MarketPlaceType } from "@vahak/core/dist/constants";
import { ROUTES } from "@vahak/core/dist/constants/routeStrings";

import { getHomeUrl } from "../../../../methods/pageSeo";
import { CityPageLocalizedContent } from "../../../city/data/types";
import {
    getMarketplaceInCityTextForSeoContent,
    getMarketplaceLoadFaqList,
    MarketplaceFaqItem,
    MarketplaceLoadSeoContext,
    resolveMarketplaceLoadSeoContext
} from "../../../marketplace-new/marketplacePageHelpers";
import useMarketplaceV3Translation from "./useMarketplaceV3Translation";

export type MarketplaceV3PageMeta = {
    title: string;
    description: string;
    keywords?: string;
    ogImgAltText: string;
    breadcrumbLabel?: string;
};

const EMPTY_LOCAL_RESOURCES = {
    title: "",
    dhabas: [],
    contacts: [],
    moreToggle: "",
    note: ""
};

function asStringArray(value: unknown) {
    return Array.isArray(value) ? (value as string[]) : [];
}

function buildLorryLandingContent(
    t: ReturnType<typeof useMarketplaceV3Translation>["t"],
    seoContext: MarketplaceLoadSeoContext,
    faqList: MarketplaceFaqItem[],
    pageMeta: MarketplaceV3PageMeta
): CityPageLocalizedContent {
    let headline2Lines: string[] = [];
    let heroSub = "";
    let aboutText = "";
    let tags: string[] = [];
    let footerSeoText = "";

    switch (seoContext.kind) {
        case "city":
        case "destination":
            headline2Lines = [t("lorry.hero.headline2WithCity", { city: seoContext.cityDisplayName })];
            heroSub = t("lorry.hero.heroSubWithCity", { city: seoContext.cityDisplayName });
            aboutText = t("lorry.about.textWithCity", { city: seoContext.cityDisplayName });
            tags = asStringArray(
                t("lorry.about.tagsWithCity", { returnObjects: true, city: seoContext.cityDisplayName })
            );
            footerSeoText = t("lorry.footer.seoTextWithCity", { city: seoContext.cityDisplayName });
            break;
        case "route":
            headline2Lines = [
                t("lorry.hero.headline2RouteFrom", { fromCity: seoContext.fromCity }),
                t("lorry.hero.headline2RouteTo", { toCity: seoContext.toCity })
            ];
            heroSub = t("lorry.hero.heroSubWithRoute", {
                fromCity: seoContext.fromCity,
                toCity: seoContext.toCity
            });
            aboutText = t("lorry.about.textWithRoute", {
                fromCity: seoContext.fromCity,
                toCity: seoContext.toCity
            });
            tags = asStringArray(
                t("lorry.about.tagsWithRoute", {
                    returnObjects: true,
                    fromCity: seoContext.fromCity,
                    toCity: seoContext.toCity
                })
            );
            footerSeoText = t("lorry.footer.seoTextWithRoute", {
                fromCity: seoContext.fromCity,
                toCity: seoContext.toCity
            });
            break;
        default:
            headline2Lines = asStringArray(t("lorry.hero.headline2Default", { returnObjects: true }));
            heroSub = t("lorry.hero.heroSubDefault");
            aboutText = t("lorry.about.textDefault");
            tags = asStringArray(t("lorry.about.tagsDefault", { returnObjects: true }));
            footerSeoText = t("lorry.footer.seoTextDefault");
            break;
    }

    return {
        seo: {
            title: pageMeta.title,
            description: pageMeta.description
        },
        pickupCity: "",
        dropPlaceholder: t("lorry.hero.dropPlaceholder"),
        heroEyebrow: t("lorry.hero.eyebrow"),
        heroHeadline1: t("lorry.hero.headline1"),
        pickupPlaceholder: t("lorry.hero.pickupPlaceholder"),
        heroHeadline2Lines: headline2Lines,
        heroSub,
        breadcrumbCityName: pageMeta.breadcrumbLabel ?? "Lorry Marketplace",
        trustStrip: "",
        aboutTitle: t("lorry.about.title"),
        aboutText,
        tags,
        proofStats: [],
        localResources: EMPTY_LOCAL_RESOURCES,
        faqs: faqList,
        popularRoutesLabel: "",
        popularRoutes: [],
        vehicleTypesLabel: "",
        vehicleTypes: [],
        footerSeoText,
        footerGroups: []
    };
}

export function useMarketplaceV3PageSeo(
    urlSrc: string,
    urlDest: string,
    marketplaceType: MarketPlaceType = MarketPlaceType.LOAD
) {
    const { t } = useMarketplaceV3Translation();
    const router = useRouter();
    const isLorryMarketplace = marketplaceType === MarketPlaceType.LORRY;
    const copyPrefix = isLorryMarketplace ? "lorry." : "";

    const locationSegment = Array.isArray(router.query.params) ? router.query.params[1] : undefined;
    const resolvedLocationSegment = typeof locationSegment === "string" ? locationSegment : undefined;
    const hasLoadLorryDetailQuery = Boolean(router.query.loadbid || router.query.lorrybid);

    const genericFaqList = useMemo(() => {
        const items = t(`${copyPrefix}faq.items`, { returnObjects: true });
        return (Array.isArray(items) ? items : []) as MarketplaceFaqItem[];
    }, [copyPrefix, t]);

    const seoContext = useMemo(
        () =>
            resolveMarketplaceLoadSeoContext({
                locationSegment: resolvedLocationSegment,
                urlSrc,
                urlDest
            }),
        [resolvedLocationSegment, urlSrc, urlDest]
    );

    const faqList = useMemo(
        () =>
            getMarketplaceLoadFaqList({
                locationSegment: resolvedLocationSegment,
                urlSrc,
                urlDest,
                genericFaqList
            }),
        [genericFaqList, resolvedLocationSegment, urlDest, urlSrc]
    );

    const pageMeta = useMemo((): MarketplaceV3PageMeta => {
        switch (seoContext.kind) {
            case "city":
            case "destination":
                return {
                    title: t(`${copyPrefix}meta.titleWithCity`, { city: seoContext.cityDisplayName }),
                    description: t(`${copyPrefix}meta.descriptionWithCity`, { city: seoContext.cityDisplayName }),
                    keywords: t(`${copyPrefix}meta.keywordsWithCity`, { city: seoContext.cityDisplayName }),
                    ogImgAltText: t(`${copyPrefix}meta.ogImgAltTextWithCity`, { city: seoContext.cityDisplayName }),
                    breadcrumbLabel: seoContext.cityDisplayName
                };
            case "route":
                return {
                    title: t(`${copyPrefix}meta.titleWithRoute`, {
                        fromCity: seoContext.fromCity,
                        toCity: seoContext.toCity
                    }),
                    description: t(`${copyPrefix}meta.descriptionWithRoute`, {
                        fromCity: seoContext.fromCity,
                        toCity: seoContext.toCity
                    }),
                    keywords: t(`${copyPrefix}meta.keywordsWithCity`, { city: seoContext.routeLabel }),
                    ogImgAltText: t(`${copyPrefix}meta.ogImgAltTextWithCity`, { city: seoContext.routeLabel }),
                    breadcrumbLabel: seoContext.routeLabel
                };
            default:
                return {
                    title: t(`${copyPrefix}meta.title`),
                    description: t(`${copyPrefix}meta.description`),
                    keywords: undefined,
                    ogImgAltText: t(`${copyPrefix}meta.ogImgAltText`),
                    breadcrumbLabel: undefined
                };
        }
    }, [copyPrefix, seoContext, t]);

    const inCityTextWithoutIndia = useMemo(
        () =>
            getMarketplaceInCityTextForSeoContent({
                locationSegment: resolvedLocationSegment,
                urlSrc,
                urlDest
            }),
        [resolvedLocationSegment, urlDest, urlSrc]
    );

    const marketplaceSeoPath = ROUTES.marketplace.getUrl(marketplaceType, urlSrc, urlDest);

    const breadcrumbItems = useMemo(
        () => [
            { name: "Home", url: getHomeUrl() },
            {
                name: isLorryMarketplace ? "Lorry Marketplace" : "Load Marketplace",
                path: ROUTES.marketplace.getUrl(marketplaceType)
            },
            ...(pageMeta.breadcrumbLabel ? [{ name: pageMeta.breadcrumbLabel }] : [])
        ],
        [isLorryMarketplace, marketplaceType, pageMeta.breadcrumbLabel]
    );

    const landingContent = useMemo(
        () => (isLorryMarketplace ? buildLorryLandingContent(t, seoContext, faqList, pageMeta) : undefined),
        [faqList, isLorryMarketplace, pageMeta, seoContext, t]
    );

    return {
        asPath: router.asPath,
        breadcrumbItems,
        faqList,
        inCityTextWithoutIndia,
        landingContent,
        marketplaceSeoPath,
        pageMeta,
        shouldBeIndexed: !hasLoadLorryDetailQuery
    };
}
